import { STUDIO_SCRATCH_ID } from 'src/components/star-grid/hooks/useStudioSelection';
import { unresolvedStudioContext } from 'src/components/star-grid/utils/studioContext';
import { isStudioBindingSelectable } from 'src/components/star-grid/utils/studioBindings';
import type {
  StudioCatalog,
  StudioDefinition,
  StudioDeck,
  StudioField,
  StudioDataset,
  StudioColumn,
  StudioLineSet
} from 'src/components/star-grid/reportStudio.types';
import type { StarGridTextKey } from 'src/components/star-grid/hooks/useStarGridText';
export interface StudioIssue {
  readonly path: string;
  readonly key: StarGridTextKey;
}
const ids = (items: readonly { id: string }[]) => new Set(items.map((item) => item.id));
const duplicates = (items: readonly { id: string }[]) => ids(items).size !== items.length;
const issue = (path: string, key: StarGridTextKey): StudioIssue => ({ path, key });
const fieldIssues = (field: StudioField, dataset: StudioDataset, deck: StudioDeck): StudioIssue[] => {
  const binding = dataset.bindings.find((item) => item.id === field.binding_id);
  if (!binding) return [issue(field.id, 'studioInvalidBinding')];
  if (!isStudioBindingSelectable(dataset, binding.id)) return [issue(field.id, 'studioHierarchyUnavailable')];
  const selected = field.selection;
  if (selected.kind === 'members' && !selected.members.length) return [issue(field.id, 'studioChooseMembers')];
  if (['children', 'descendants'].includes(selected.kind) && selected.members.length !== 1) return [issue(field.id, 'studioChooseRoot')];
  if (selected.kind === 'subset' && !binding.subsets.some((item) => item.id === selected.reference_id))
    return [issue(field.id, 'studioChooseSubset')];
  // The deck's rows SHARE one line set, so the set is owned by the FIRST row dimension: a later one is
  // resolved by the lines that came from it rather than by the set's own binding.
  if (selected.kind === 'line_set') {
    const set = deck.line_sets.find((item) => item.id === selected.reference_id);
    if (!set || (set.binding_id !== binding.id && !set.entries.some((entry) => entry.binding_id === binding.id)))
      return [issue(field.id, 'studioChooseLineSet')];
  }
  return [];
};
const columnIssues = (column: StudioColumn, deck: StudioDeck, dataset: StudioDataset, catalog: StudioCatalog): StudioIssue[] => {
  if (column.kind === 'attribute') {
    const row = deck.rows.find((field) => field.id === column.row_instance_id);
    const binding = dataset.bindings.find((item) => item.id === row?.binding_id);
    return binding?.attributes.some((attribute) => attribute.id === column.attribute_id) ? [] : [issue(column.id, 'studioChooseAttribute')];
  }
  if (column.fx_policy_id && !dataset.fx_policies.some((item) => item.id === column.fx_policy_id)) return [issue(column.id, 'studioInvalidFx')];
  if (column.kind === 'variance') return comparisonIssues(column, deck);
  if (!catalog.scenarios.some((item) => item.id === column.scenario_id)) return [issue(column.id, 'studioChooseScenario')];
  if (!column.period_ids.length || column.period_ids.some((id) => !catalog.periods.some((period) => period.id === id)))
    return [issue(column.id, 'studioChoosePeriods')];
  if (column.version_id && !catalog.versions.some((item) => item.id === column.version_id)) return [issue(column.id, 'studioChooseVersion')];
  if (!dataset.metrics.some((item) => item.id === column.metric_id)) return [issue(column.id, 'studioChooseMetric')];
  return column.currency_ids.some((id) => !dataset.currencies.some((item) => item.id === id)) ? [issue(column.id, 'studioChooseCurrency')] : [];
};
const comparisonIssues = (column: Extract<StudioColumn, { kind: 'variance' }>, deck: StudioDeck): StudioIssue[] => {
  const base = deck.columns.find((item) => item.id === column.base_block_id);
  const compare = deck.columns.find((item) => item.id === column.compare_block_id);
  if (base?.kind !== 'scenario' || compare?.kind !== 'scenario' || base.id === compare.id) return [issue(column.id, 'studioChooseComparison')];
  return !column.period_pairs.length ||
    column.period_pairs.some((pair) => !base.period_ids.includes(pair.base) || !compare.period_ids.includes(pair.compare))
    ? [issue(column.id, 'studioInvalidPairing')]
    : [];
};
export const studioLineIssues = (set: StudioLineSet): StudioIssue[] => {
  if (!set.editable) return [];
  const entries = new Map(set.entries.map((line) => [line.id, line]));
  const visit = (id: string, trail: Set<string>): boolean => {
    if (trail.has(id)) return true;
    return (entries.get(id)?.references ?? []).some((next) => visit(next, new Set([...trail, id])));
  };
  if (duplicates(set.entries)) return [issue(set.id, 'studioDuplicateIdentity')];
  for (const line of set.entries) {
    if (line.kind === 'member' && !line.member_id) return [issue(line.id, 'studioChooseMembers')];
    if (!['calculation', 'ratio'].includes(line.kind)) continue;
    const refs = line.references ?? [];
    if (
      !line.operator ||
      !refs.length ||
      (line.operator !== 'sum' && refs.length !== 2) ||
      refs.some((ref) => !['member', 'calculation', 'ratio'].includes(entries.get(ref)?.kind ?? ''))
    )
      return [issue(line.id, 'studioInvalidFormula')];
    if (line.weights && (line.weights.length !== refs.length || line.weights.some((weight) => !Number.isFinite(weight))))
      return [issue(line.id, 'studioInvalidFormula')];
    if (visit(line.id, new Set())) return [issue(line.id, 'studioFormulaCycle')];
  }
  return [];
};
export const studioIssues = (definition: StudioDefinition, catalog: StudioCatalog): StudioIssue[] => {
  const dataset = catalog.datasets.find((item) => item.id === definition.dataset_id);
  if (!dataset) return [issue('dataset', 'studioChooseDataset')];
  const issues: StudioIssue[] = Object.entries(definition.fixed_context)
    .filter(([, value]) => unresolvedStudioContext(value))
    .map(([key]) => issue(`fixed_context.${key}`, 'studioContextUnresolved'));
  const template = catalog.templates.find((item) => item.id === definition.template_id);
  // A from-scratch report has no template to be ready.
  if (definition.template_id !== STUDIO_SCRATCH_ID && !template?.ready) issues.push(issue('template', 'studioTemplateNotReady'));
  if (!definition.deck_types.length || !definition.deck_types.includes(definition.active_deck)) issues.push(issue('decks', 'studioChooseDecks'));
  if (!catalog.versions.some((item) => item.id === definition.default_version_id)) issues.push(issue('version', 'studioChooseVersion'));
  for (const type of definition.deck_types) {
    const deck = definition.decks[type];
    if (!deck) {
      issues.push(issue(type, 'studioMissingDeck'));
      continue;
    }
    if (!deck.rows.length || !deck.columns.some((column) => column.kind === 'scenario')) issues.push(issue(type, 'studioNeedRowsColumns'));
    if (duplicates([...deck.rows, ...deck.filters, ...deck.columns])) issues.push(issue(type, 'studioDuplicateIdentity'));
    issues.push(...[...deck.rows, ...deck.filters].flatMap((field) => fieldIssues(field, dataset, deck)));
    issues.push(...deck.columns.flatMap((column) => columnIssues(column, deck, dataset, catalog)), ...deck.line_sets.flatMap(studioLineIssues));
  }
  return issues;
};
